import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { USAGE_PATH } from '../../app/nav';
import { Icon } from '../../components/icons';
import { Alert, buttonClasses } from '../../components/ui';
import { useToast } from '../../components/ui/toast';
import {
  ApiError,
  billingApi,
  workspaceApi,
  type CatalogueEntry,
  type CurrentWorkspace,
  type PlanFeatureKey,
  type PlanLimitKey,
  type PlanOverview,
  type UpgradeTarget,
} from '../../lib/api';
import { formatNumber } from '../../lib/format';

/**
 * The plan as the tenant UI mirrors it (D-183). The server is the authority:
 * a gated write answers 402, and everything here only reflects what
 * `GET /workspaces/current` and `GET /billing/plan` say. A feature is locked
 * only when the plan says `false`; while loading, or on an error, nothing is.
 * The plan a lock or a 402 points to is read from the catalogue, never from a
 * table here, so moving a feature between plans needs no code (brief §2).
 */

export const CURRENT_WORKSPACE_KEY = ['workspace', 'current'] as const;
export const PLAN_OVERVIEW_KEY = ['billing', 'plan'] as const;

/** The workspace, its suspension and its plan's features; the key every page already loads. */
export function useCurrentWorkspace() {
  return useQuery({ queryKey: CURRENT_WORKSPACE_KEY, queryFn: workspaceApi.current });
}

export interface FeatureState {
  /** True only when the plan explicitly turns `key` off. */
  isLocked: (key: PlanFeatureKey) => boolean;
  /** How far back analytics may be read; null = unlimited or not known yet. */
  maxAnalyticsDays: number | null;
  role: CurrentWorkspace['role'] | null;
}

export function useFeatures(): FeatureState {
  const { data } = useCurrentWorkspace();
  // `plan` may be absent from an older or partial answer: nothing is locked then.
  const plan = data?.plan;
  return {
    isLocked: (key) => plan?.features?.[key] === false,
    maxAnalyticsDays: plan?.maxAnalyticsDays ?? null,
    role: data?.role ?? null,
  };
}

/**
 * Whether `feature` is off, and the plan `GET /workspaces/current` names to
 * open it (`plan.upgradeFor`, every role, no catalogue call). A key absent
 * from an older answer reads as null.
 */
export function useUpgradeTarget(feature: PlanFeatureKey): { locked: boolean; target: UpgradeTarget | null } {
  const plan = useCurrentWorkspace().data?.plan;
  return { locked: plan?.features?.[feature] === false, target: plan?.upgradeFor?.[feature] ?? null };
}

/** `GET /billing/plan`: owner and admins only — never requested for an agent (403), nor before the role is known. */
export function usePlanOverview({ enabled = true }: { enabled?: boolean } = {}) {
  const role = useCurrentWorkspace().data?.role;
  return useQuery({
    queryKey: PLAN_OVERVIEW_KEY,
    queryFn: billingApi.plan,
    enabled: enabled && role !== undefined && role !== 'agent',
    retry: false,
  });
}

// --- 402s -----------------------------------------------------------------------------------

/** Why the server refused: a feature off on the plan, or a quantity at its cap. */
export type PlanBlock =
  | { kind: 'feature'; feature: string; limitDays: number | null }
  | { kind: 'limit'; limitName: string; limit: number | null };

/** The plan block an error carries (402 `FEATURE_NOT_IN_PLAN` or `LIMIT_REACHED`), or null. */
export function planBlockOf(error: unknown): PlanBlock | null {
  if (!(error instanceof ApiError) || error.status !== 402) return null;
  const details = error.details ?? {};
  if (error.code === 'FEATURE_NOT_IN_PLAN') {
    return {
      kind: 'feature',
      feature: typeof details.feature === 'string' ? details.feature : '',
      limitDays: typeof details.limitDays === 'number' ? details.limitDays : null,
    };
  }
  if (error.code === 'LIMIT_REACHED') {
    return {
      kind: 'limit',
      limitName: typeof details.limitName === 'string' ? details.limitName : '',
      limit: typeof details.limit === 'number' ? details.limit : null,
    };
  }
  return null;
}

export const isPlanBlock = (error: unknown): boolean => planBlockOf(error) !== null;

const MONTHS: Record<CatalogueEntry['interval'], number> = { month: 1, quarter: 3, year: 12 };

/** Price per month, the server's rule for ranking plans (D-183, `compareMonthlyPrice`). */
export const monthlyPrice = (plan: Pick<CatalogueEntry, 'priceAmount' | 'interval'>): number => plan.priceAmount / MONTHS[plan.interval];

const isLimitKey = (plans: CatalogueEntry[], name: string): name is PlanLimitKey => plans.some((p) => name in p.limits);

/**
 * The cheapest monthly plan of the catalogue that lifts the block: the first
 * with the feature on, or with a higher limit (null = unlimited). Analytics
 * history is a quantity behind a feature code: the first keeping more days.
 */
export function nextPlanFor(catalogue: CatalogueEntry[], block: PlanBlock | null): CatalogueEntry | null {
  if (!block) return null;
  const monthly = catalogue.filter((p) => p.interval === 'month').sort((a, b) => monthlyPrice(a) - monthlyPrice(b));
  const higher = (value: number | null | undefined, than: number | null) => value === null || (value !== undefined && than !== null && value > than);
  if (block.kind === 'feature') {
    if (block.feature === 'analyticsHistory') return monthly.find((p) => higher(p.limits.maxAnalyticsDays, block.limitDays)) ?? null;
    return monthly.find((p) => (p.flags as Record<string, boolean | undefined>)[block.feature] === true) ?? null;
  }
  const { limitName } = block;
  if (!isLimitKey(monthly, limitName)) return null;
  return monthly.find((p) => higher(p.limits[limitName], block.limit)) ?? null;
}

/** Usage meters by limit name: the 402 says "Automatisations", not "maxAutomations". */
const METER_OF: Partial<Record<string, 'contacts' | 'users' | 'inboxSeats' | 'channels' | 'automations' | 'activeAutomations' | 'customFields' | 'executions' | 'messages'>> = {
  maxContacts: 'contacts',
  maxUsers: 'users',
  maxInboxSeats: 'inboxSeats',
  maxChannels: 'channels',
  maxAutomations: 'automations',
  maxActiveAutomations: 'activeAutomations',
  maxCustomFields: 'customFields',
  maxExecutionsPerMonth: 'executions',
  maxMessagesPerMonth: 'messages',
};

const FEATURE_MESSAGES = ['scheduledPosts', 'campaigns', 'segments', 'httpRequestStep', 'apiAccess'] as const;

/** The 402 in the reader's language: the server's messages are English (D-183). */
export function planBlockText(block: PlanBlock, t: TFunction): { title: string; body: string } {
  if (block.kind === 'feature') {
    const known = (FEATURE_MESSAGES as readonly string[]).includes(block.feature) ? (block.feature as (typeof FEATURE_MESSAGES)[number]) : null;
    const body =
      block.feature === 'analyticsHistory' && block.limitDays !== null
        ? t('planBlock.feature.analyticsHistory', { days: block.limitDays })
        : known
          ? t(`planBlock.feature.${known}`)
          : t('planBlock.feature.other');
    return { title: t('planBlock.featureTitle'), body };
  }
  const meter = METER_OF[block.limitName];
  const body =
    meter && block.limit !== null
      ? t('planBlock.limit', { label: t(`usage.meters.${meter}`), limit: formatNumber(block.limit) })
      : t('planBlock.limitOther');
  return { title: t('planBlock.limitTitle'), body };
}

/** Mon offre with a plan picked, or plain when there is none to suggest. */
export const plansPath = (plan: Pick<CatalogueEntry, 'code'> | null): string => (plan ? `${USAGE_PATH}?plan=${encodeURIComponent(plan.code)}` : USAGE_PATH);

const DEDUPE_MS = 3000;

/**
 * Shows a 402 as one toast with "Changer d'offre" (owner and admins) or
 * "Demandez au propriétaire" (agents). Returns whether the error was one, so
 * a caller with its own error line can leave it to the toast. The catalogue
 * is only fetched here, to name the plan.
 */
export function usePlanBlockReporter(): (error: unknown) => boolean {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const last = useRef<{ key: string; at: number } | null>(null);

  return useCallback(
    (error: unknown) => {
      const block = planBlockOf(error);
      if (!block) return false;
      // The same refusal twice in a row (a double click) is one message.
      const key = JSON.stringify(block);
      const now = Date.now();
      if (last.current && last.current.key === key && now - last.current.at < DEDUPE_MS) return true;
      last.current = { key, at: now };

      const { title, body } = planBlockText(block, t);
      void (async () => {
        const workspace = await queryClient
          .fetchQuery({ queryKey: CURRENT_WORKSPACE_KEY, queryFn: workspaceApi.current, staleTime: 60_000 })
          .catch(() => null);
        if (workspace?.role === 'agent') {
          toast({ title, body: `${body} ${t('planBlock.askOwner')}`, tone: 'info' });
          return;
        }
        const overview = await queryClient
          .fetchQuery<PlanOverview>({ queryKey: PLAN_OVERVIEW_KEY, queryFn: billingApi.plan, staleTime: 60_000, retry: false })
          .catch(() => null);
        const target = plansPath(overview ? nextPlanFor(overview.catalogue, block) : null);
        toast({ title, body, tone: 'info', action: { label: t('planBlock.change'), onClick: () => navigate(target) } });
      })();
      return true;
    },
    [queryClient, toast, navigate, t],
  );
}

/**
 * A mutation's `meta` for the one page that says its 402 in line instead
 * (accepting an invitation: the refusing workspace is not the reader's, so
 * "Changer d'offre" would point at the wrong one). The toaster skips it.
 */
export const PLAN_BLOCK_SHOWN_INLINE = { planBlockShownInline: true } as const;

/**
 * The one place a refused write turns into the toast (D-183): every mutation
 * of the app goes through the mutation cache, so this listener sees each 402
 * once, except one marked `PLAN_BLOCK_SHOWN_INLINE`. Mounted under the router
 * and the toasts.
 */
export function PlanBlockToaster() {
  const queryClient = useQueryClient();
  const report = usePlanBlockReporter();
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (event.type !== 'updated' || event.action.type !== 'error') return;
        if (event.mutation.options.meta?.planBlockShownInline === true) return;
        report(event.action.error);
      }),
    [queryClient, report],
  );
  return null;
}

/** For a refused read shown inline (analytics history): the same way out as the toast. */
export function PlanBlockAction({ error }: { error: unknown }) {
  return <PlanBlockLink block={planBlockOf(error)} />;
}

/** The same way out for a block known before any write (the invite form at its user limit). */
export function PlanBlockLink({ block }: { block: PlanBlock | null }) {
  const { t } = useTranslation();
  const { role } = useFeatures();
  const overview = usePlanOverview({ enabled: block !== null });
  if (!block || role === null) return null;
  if (role === 'agent') return <span className="text-ds-sm">{t('planBlock.askOwner')}</span>;
  return (
    <Link to={plansPath(overview.data ? nextPlanFor(overview.data.catalogue, block) : null)} className={`touch-target ${buttonClasses('secondary', 'sm')}`}>
      {t('planBlock.change')}
    </Link>
  );
}

// --- Locks ----------------------------------------------------------------------------------

/**
 * "Disponible à partir de Pro" with "Voir les offres" (brief §2: visible, not
 * hidden). The plan named is the catalogue's cheapest with the feature on; an
 * agent cannot read the catalogue and is sent to the owner instead.
 */
export function FeatureLock({ feature, className = '' }: { feature: PlanFeatureKey; className?: string }) {
  const { t } = useTranslation();
  const { role } = useFeatures();
  const overview = usePlanOverview();
  const plan = overview.data ? nextPlanFor(overview.data.catalogue, { kind: 'feature', feature, limitDays: null }) : null;
  const isAgent = role === 'agent';
  return (
    <Alert
      tone="neutral"
      className={className}
      action={
        isAgent ? undefined : (
          <Link to={plansPath(plan)} className={`touch-target ${buttonClasses('secondary', 'sm')}`}>
            {t('planLock.seePlans')}
          </Link>
        )
      }
    >
      <span className="flex items-start gap-2">
        <span role="img" aria-label={t('planLock.lockLabel')} className="mt-px shrink-0">
          <Icon name="lock" className="size-[18px]" />
        </span>
        <span className="min-w-0">
          <b className="font-semibold text-ink">{t(`planLock.title.${feature}`)}</b>{' '}
          {isAgent ? t('planLock.askOwner') : plan ? t('planLock.availableFrom', { plan: plan.name }) : t('planLock.notIncluded')}
        </span>
      </span>
    </Alert>
  );
}

/** The short reason a disabled create button gives: the plan to move to, or that it is not included. */
export function useLockReason(feature: PlanFeatureKey): string | null {
  const { t } = useTranslation();
  const { isLocked, role } = useFeatures();
  const locked = isLocked(feature);
  const overview = usePlanOverview({ enabled: locked });
  if (!locked) return null;
  const plan = overview.data ? nextPlanFor(overview.data.catalogue, { kind: 'feature', feature, limitDays: null }) : null;
  return role !== 'agent' && plan ? t('planLock.availableFrom', { plan: plan.name }) : t('planLock.notIncluded');
}
